import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useApi } from '../hooks/useApi';
import { useAnalyticsPackage } from '../context/AnalyticsPackageContext';
import { useUserContext } from '../context/UserContext';
import prismLogo from '../assets/prism-logo.svg';

const PRISM_LOGO_ASPECT = 1179 / 375.935;

function loadImage(src: string, crossOrigin = false): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function fetchSvgAsDataUrl(svgSrc: string, pixW: number, pixH: number): Promise<string | null> {
  try {
    const res = await fetch(svgSrc);
    let text = await res.text();
    text = text.replace(/<svg\b/, `<svg width="${pixW}" height="${pixH}"`);
    const blob = new Blob([text], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const img = await loadImage(url);
    URL.revokeObjectURL(url);
    const c = document.createElement('canvas');
    c.width = pixW; c.height = pixH;
    c.getContext('2d')!.drawImage(img, 0, 0);
    return c.toDataURL('image/png');
  } catch {
    return null;
  }
}

const PROPERTY_TYPES = [
  { value: '1F', label: 'Single Family' },
  { value: 'C',  label: 'Condominium' },
  { value: 'L',  label: 'Land' },
];

const BUILDING_CATEGORIES = [
  { value:  0, label: 'All Building Categories' },
  { value: -1, label: 'All Luxury' },
  { value: -2, label: '   Luxury Full Service' },
  { value:  1, label: '      Luxe Hotel' },
  { value:  2, label: '      Luxe Classic' },
  { value:  3, label: '      Luxe Boutique' },
  { value:  4, label: '   Mid Luxe' },
  { value:  7, label: 'Traditional' },
];

const ACTIVE_BG   = '#958945';
const ACTIVE_TEXT = '#ffffff';
const LOCKED_BG   = '#f0f0f0';
const LOCKED_TEXT = '#999999';

interface Market {
  id: number;
  name: string;
  hasAccess: boolean;
  defaultPropertyType: string | null;
  photoUrl: string | null;
}

interface Neighborhood {
  id: number;
  name: string;
  hasAccess: boolean;
  photoUrl: string | null;
}

interface ChartOption {
  id: number;
  name: string;
  title: string;
  description: string | null;
}

interface RollingWindowDto {
  label: string;
  sales: number | null;
  volume: number | null;
  avgPrice: number | null;
  medianPrice: number | null;
  avgPsf: number | null;
  medianPsf: number | null;
  deltaSales: number | null;
  deltaVolume: number | null;
  deltaAvgPrice: number | null;
  deltaMedianPrice: number | null;
  deltaAvgPsf: number | null;
  deltaMedianPsf: number | null;
}

interface BuildingRow {
  buildingId: number;
  building: string;
  buildingCategory: string;
  neighborhoodId: number;
  neighborhood: string;
  buildingAddress: string | null;
  units: number | null;
  annualizedAppreciation: number | null;
}

interface AllBuildingsRow {
  buildingId: number;
  building: string;
  buildingCategory: string;
  neighborhoodId: number;
  neighborhood: string;
  buildingAddress: string | null;
  units: number | null;
}

interface Subarea {
  id: number;
  name: string;
}

interface NeighborhoodOverviewRow {
  neighborhoodId: number;
  neighborhoodName: string;
  avgPrice: number | null;
  medianPrice: number | null;
  avgPsf: number | null;
  medianPsf: number | null;
  deltaAvgPrice: number | null;
  deltaMedianPrice: number | null;
  deltaAvgPsf: number | null;
  deltaMedianPsf: number | null;
}

interface Props {
  mode: 'market' | 'neighborhood';
  id: number;
}

const MONTH_ABBR: Record<string, string> = {
  Jan: 'January', Feb: 'February', Mar: 'March',    Apr: 'April',
  May: 'May',     Jun: 'June',     Jul: 'July',      Aug: 'August',
  Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};
function expandMonth(abbr: string): string {
  return abbr.replace(/^[A-Z][a-z]{2}/, m => MONTH_ABBR[m] ?? m);
}

function fmtPrice(v: number | null): string {
  if (v == null) return '—';
  return `$${Math.round(v).toLocaleString('en-US')}`;
}

function fmtVolume(v: number | null): string {
  if (v == null) return '—';
  return v >= 1_000_000_000
    ? `$${(v / 1_000_000_000).toFixed(1)}B`
    : `$${(v / 1_000_000).toFixed(1)}M`;
}

function fmtCount(v: number | null): string {
  if (v == null) return '—';
  return v.toLocaleString('en-US');
}

function fmtDelta(v: number | null): string | null {
  if (v == null) return null;
  if (v > 0) return `▲ ${v.toFixed(1)}%`;
  if (v < 0) return `▼ ${Math.abs(v).toFixed(1)}%`;
  return null;
}

function deltaColor(v: number | null): string {
  if (v == null) return '#666';
  return v >= 0 ? '#1CA11C' : 'rgb(214,10,34)';
}

function sortableBuildingName(name: string): string {
  return name.replace(/^the\s+/i, '');
}

export function LocationLandingPage({ mode, id }: Props) {
  const navigate = useNavigate();
  const { authFetch } = useApi();
  const { addItem } = useAnalyticsPackage();
  const { showPdfLens, me } = useUserContext();

  const [markets, setMarkets] = useState<Market[]>([]);
  const [neighborhoodsByMarket, setNeighborhoodsByMarket] = useState<Record<number, Neighborhood[]>>({});
  const [expandedMarkets, setExpandedMarkets] = useState<number[]>([]);
  const [stats, setStats] = useState<RollingWindowDto[] | null>(null);
  const [chartOptions, setChartOptions] = useState<ChartOption[]>([]);
  const [buildings, setBuildings] = useState<BuildingRow[] | null>(null);
  const [buildingSortCol, setBuildingSortCol] = useState<'building' | 'neighborhood' | 'address' | 'units' | 'appreciation'>('building');
  const [buildingSortDir, setBuildingSortDir] = useState<'asc' | 'desc'>('asc');
  const [buildingPage, setBuildingPage] = useState(1);
  const [neighborhoodOverview, setNeighborhoodOverview] = useState<NeighborhoodOverviewRow[] | null>(null);
  const [propertyType, setPropertyType] = useState(() => new URLSearchParams(window.location.search).get('propertyType') || 'C');
  const [buildingCategoryId, setBuildingCategoryId] = useState<number | null>(() => Number(new URLSearchParams(window.location.search).get('buildingCategoryId')) || null);
  const [subareas, setSubareas] = useState<Subarea[]>([]);
  const [selectedSubarea, setSelectedSubarea] = useState<Subarea | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const owningMarketIdRef = useRef<number | null>(null);
  const urlPropertyTypeRef = useRef(new URLSearchParams(window.location.search).get('propertyType'));
  const urlSubareaIdRef = useRef(Number(new URLSearchParams(window.location.search).get('subareaId')) || null);
  const initialIdRef = useRef(id);
  const statsRef = useRef<HTMLDivElement>(null);
  const statsPdfRef = useRef<HTMLDivElement>(null);
  const neighborhoodTableRef = useRef<HTMLDivElement>(null);
  const neighborhoodPdfRef = useRef<HTMLDivElement>(null);
  const [orgLogoUrl, setOrgLogoUrl] = useState<string | undefined>(undefined);

  // Load markets + all accessible neighborhoods eagerly (needed to resolve neighborhood→market)
  useEffect(() => {
    authFetch('/api/markets')
      .then(r => r.json())
      .then(async (mktData: Market[]) => {
        setMarkets(mktData);
        const accessible = mktData.filter(m => m.hasAccess);

        const entries = await Promise.all(
          accessible.map(m =>
            authFetch(`/api/markets/${m.id}/neighborhoods`)
              .then(r => r.json())
              .then((nhs: Neighborhood[]) => [m.id, nhs] as [number, Neighborhood[]])
          )
        );
        const nhMap: Record<number, Neighborhood[]> = {};
        entries.forEach(([mid, nhs]) => { nhMap[mid] = nhs; });
        setNeighborhoodsByMarket(nhMap);

        let owningMktId: number | null = null;
        if (mode === 'market') {
          owningMktId = id;
          setExpandedMarkets([id]);
        } else {
          const key = Object.keys(nhMap).find(mid =>
            nhMap[Number(mid)].some(n => n.id === id)
          );
          if (key) {
            owningMktId = Number(key);
            setExpandedMarkets([owningMktId]);
          }
        }
        owningMarketIdRef.current = owningMktId;

        if (owningMktId) {
          authFetch(`/api/charts?marketId=${owningMktId}`)
            .then(r => r.json())
            .then(setChartOptions);
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply owning market's default property type whenever the location changes.
  // While still on the id this component mounted with, honor the URL param instead of overriding.
  // Once the user navigates to a different location via the accordion, use the market default.
  useEffect(() => {
    if (markets.length === 0) return;
    if (urlPropertyTypeRef.current !== null && id === initialIdRef.current) {
      return;
    }
    let owningMarket: Market | undefined;
    if (mode === 'market') {
      owningMarket = markets.find(m => m.id === id);
    } else {
      const key = Object.keys(neighborhoodsByMarket).find(mid =>
        neighborhoodsByMarket[Number(mid)].some(n => n.id === id)
      );
      if (key) owningMarket = markets.find(m => m.id === Number(key));
    }
    setPropertyType(owningMarket?.defaultPropertyType ?? 'C');
  }, [id, mode, markets, neighborhoodsByMarket]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch subareas for neighborhood mode (re-runs on id change and after neighborhoods load)
  useEffect(() => {
    if (mode !== 'neighborhood') { setSubareas([]); setSelectedSubarea(null); return; }
    const owningMktKey = Object.keys(neighborhoodsByMarket).find(mid =>
      neighborhoodsByMarket[Number(mid)].some(n => n.id === id)
    );
    if (!owningMktKey) return;
    setSubareas([]);
    setSelectedSubarea(null);
    authFetch(`/api/markets/${owningMktKey}/neighborhoods/${id}/subareas`)
      .then(r => r.json())
      .then((data: Subarea[]) => {
        setSubareas(data);
        if (urlSubareaIdRef.current) {
          const match = data.find(s => s.id === urlSubareaIdRef.current);
          if (match) setSelectedSubarea(match);
          urlSubareaIdRef.current = null;
        }
      })
      .catch(() => setSubareas([]));
  }, [mode, id, neighborhoodsByMarket]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch stats whenever filters change
  useEffect(() => {
    const scopeParam   = mode === 'market' ? `marketId=${id}` : `neighborhoodId=${id}`;
    const luxParam     = buildingCategoryId === -1 ? '&luxOption=true'
                       : buildingCategoryId === -2 ? '&luxFullService=true'
                       : '';
    const catParam     = buildingCategoryId && buildingCategoryId > 0 ? `&buildingCategoryId=${buildingCategoryId}` : '';
    const subareaParam = mode === 'neighborhood' && selectedSubarea ? `&subareaId=${selectedSubarea.id}` : '';
    setStatsLoading(true);
    setStats(null);
    const controller = new AbortController();
    authFetch(`/api/dashboard/stats?${scopeParam}&propertyType=${propertyType}${luxParam}${catParam}${subareaParam}`, { signal: controller.signal })
      .then(r => r.json())
      .then((data: RollingWindowDto[]) => { setStats(data); setStatsLoading(false); })
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name !== 'AbortError') setStatsLoading(false);
      });
    return () => controller.abort();
  }, [mode, id, propertyType, buildingCategoryId, selectedSubarea]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset building page when filters change
  useEffect(() => { setBuildingPage(1); }, [mode, id, propertyType, buildingCategoryId, selectedSubarea]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch buildings — merge appreciation + statistics for a complete list
  useEffect(() => {
    if (propertyType !== 'C') { setBuildings([]); return; }
    const scopeParam   = mode === 'market' ? `marketId=${id}` : `neighborhoodId=${id}`;
    const luxParam     = buildingCategoryId === -1 ? '&luxOption=true'
                       : buildingCategoryId === -2 ? '&luxFullService=true'
                       : '';
    const catParam     = buildingCategoryId && buildingCategoryId > 0 ? `&buildingCategoryId=${buildingCategoryId}` : '';
    const subareaParam = mode === 'neighborhood' && selectedSubarea ? `&subareaId=${selectedSubarea.id}` : '';
    setBuildings(null);
    Promise.all([
      authFetch(`/api/charts/building-appreciation?${scopeParam}${luxParam}${catParam}${subareaParam}`)
        .then(r => r.json()).catch(() => [] as BuildingRow[]),
      authFetch(`/api/charts/all-buildings?${scopeParam}${luxParam}${catParam}${subareaParam}`)
        .then(r => r.json()).catch(() => [] as AllBuildingsRow[]),
    ]).then(([appreciationData, statisticsData]: [BuildingRow[], AllBuildingsRow[]]) => {
      const merged = new Map<number, BuildingRow>();
      for (const row of appreciationData) merged.set(row.buildingId, row);
      for (const row of statisticsData) {
        if (!merged.has(row.buildingId)) {
          merged.set(row.buildingId, {
            buildingId: row.buildingId,
            building: row.building,
            buildingCategory: row.buildingCategory,
            neighborhoodId: row.neighborhoodId,
            neighborhood: row.neighborhood,
            buildingAddress: row.buildingAddress,
            units: row.units,
            annualizedAppreciation: null,
          });
        }
      }
      setBuildings(Array.from(merged.values()));
    }).catch(() => setBuildings([]));
  }, [mode, id, propertyType, buildingCategoryId, selectedSubarea]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch neighborhood overview (market pages only)
  useEffect(() => {
    if (mode !== 'market') return;
    const luxParam = buildingCategoryId === -1 ? '&luxOption=true'
                   : buildingCategoryId === -2 ? '&luxFullService=true'
                   : '';
    const catParam = buildingCategoryId && buildingCategoryId > 0 ? `&buildingCategoryId=${buildingCategoryId}` : '';
    setNeighborhoodOverview(null);
    const controller = new AbortController();
    authFetch(`/api/dashboard/neighborhood-overview?marketId=${id}&propertyType=${propertyType}${luxParam}${catParam}`, { signal: controller.signal })
      .then(r => r.json())
      .then((data: NeighborhoodOverviewRow[]) => setNeighborhoodOverview(data))
      .catch((err: unknown) => {
        if ((err as { name?: string })?.name !== 'AbortError') setNeighborhoodOverview([]);
      });
    return () => controller.abort();
  }, [mode, id, propertyType, buildingCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    authFetch('/api/me')
      .then(r => r.json())
      .then((me: any) => {
        if (me?.organization?.logoUrl)
          setOrgLogoUrl(`${process.env.REACT_APP_API_URL ?? ''}/api/organizations/${me.organization.id}/logo-bytes`);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addLogos = async (pdf: jsPDF, userName?: string) => {
    const pageW = 11, pageH = 8.5;
    const prismData = await fetchSvgAsDataUrl(prismLogo as string, Math.round(PRISM_LOGO_ASPECT * 200), 200);
    if (prismData) {
      const h = 0.5;
      pdf.addImage(prismData, 'PNG', pageW - PRISM_LOGO_ASPECT * h - 0.5, pageH - h - 0.5, PRISM_LOGO_ASPECT * h, h);
    }
    if (orgLogoUrl) {
      const logoImg = await loadImage(orgLogoUrl, true).catch(() => null);
      if (logoImg) {
        const aspect = logoImg.naturalWidth / logoImg.naturalHeight;
        const c = document.createElement('canvas');
        c.width = logoImg.naturalWidth; c.height = logoImg.naturalHeight;
        c.getContext('2d')!.drawImage(logoImg, 0, 0);
        const h = 0.5;
        pdf.addImage(c.toDataURL('image/png'), 'PNG', 0.5, 0.5, aspect * h, h);
      }
    }
    if (userName) {
      const date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(160, 160, 160);
      pdf.text(`Prism Insight produced for ${userName} on ${date}`, 0.5, pageH - 0.75);
    }
  };

  const downloadMetricsPdf = async () => {
    if (!statsPdfRef.current) return;
    const canvas = await html2canvas(statsPdfRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
    const pageW = 11, pageH = 8.5, marginX = 0.5, marginTop = 1.0, marginBot = 1.0;
    const availW = pageW - 2 * marginX;
    const availH = pageH - marginTop - marginBot;
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: [pageW, pageH] });
    const widthIn = statsPdfRef.current.offsetWidth / 96;
    const heightIn = statsPdfRef.current.offsetHeight / 96;
    const fit = Math.min(availW / widthIn, availH / heightIn);
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG',
      (pageW - widthIn * fit) / 2, marginTop + (availH - heightIn * fit) / 2,
      widthIn * fit, heightIn * fit);
    const userName = me?.displayName?.trim() || me?.email || undefined;
    await addLogos(pdf, userName);
    pdf.save(`${locationName} - Metrics.pdf`);
  };

  const downloadNeighborhoodsPdf = async () => {
    if (!neighborhoodPdfRef.current) return;
    const canvas = await html2canvas(neighborhoodPdfRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
    const pageW = 11, pageH = 8.5, marginX = 0.5, marginTop = 1.0, marginBot = 1.0;
    const availW = pageW - 2 * marginX;
    const availH = pageH - marginTop - marginBot;
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: [pageW, pageH] });
    const widthIn = neighborhoodPdfRef.current.offsetWidth / 96;
    const heightIn = neighborhoodPdfRef.current.offsetHeight / 96;
    const fit = Math.min(availW / widthIn, availH / heightIn);
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG',
      (pageW - widthIn * fit) / 2, marginTop + (availH - heightIn * fit) / 2,
      widthIn * fit, heightIn * fit);
    const userName = me?.displayName?.trim() || me?.email || undefined;
    await addLogos(pdf, userName);
    pdf.save(`${locationName} - Neighborhoods.pdf`);
  };

  const currentMarket = mode === 'market' ? markets.find(m => m.id === id) : null;
  const currentNeighborhood = mode === 'neighborhood'
    ? Object.values(neighborhoodsByMarket).flat().find(n => n.id === id)
    : null;
  const locationName = currentMarket?.name ?? currentNeighborhood?.name ?? '';
  const photoUrl     = currentMarket?.photoUrl ?? currentNeighborhood?.photoUrl ?? null;

  const handleMarketToggle = (e: React.MouseEvent, marketId: number) => {
    e.stopPropagation();
    setExpandedMarkets(prev =>
      prev.includes(marketId) ? prev.filter(x => x !== marketId) : [...prev, marketId]
    );
  };

  const thStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    fontWeight: 600,
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
    textAlign: 'left',
  };
  const tdStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid #e8e8e8',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 13,
  };

  const currentPeriod = stats?.[0] ?? null;
  const currentCards: Array<{ label: string; value: string; delta: number | null }> = currentPeriod
    ? [
        { label: 'Average Price',  value: fmtPrice(currentPeriod.avgPrice),    delta: currentPeriod.deltaAvgPrice },
        { label: 'Median Price',   value: fmtPrice(currentPeriod.medianPrice), delta: currentPeriod.deltaMedianPrice },
        { label: 'Sales',          value: fmtCount(currentPeriod.sales),       delta: currentPeriod.deltaSales },
        { label: 'Volume',         value: fmtVolume(currentPeriod.volume),     delta: currentPeriod.deltaVolume },
        ...(currentPeriod.avgPsf != null    ? [{ label: 'Avg PSF',    value: fmtPrice(currentPeriod.avgPsf),    delta: currentPeriod.deltaAvgPsf }]    : []),
        ...(currentPeriod.medianPsf != null ? [{ label: 'Median PSF', value: fmtPrice(currentPeriod.medianPsf), delta: currentPeriod.deltaMedianPsf }] : []),
      ]
    : [];

  const metricsPtLabel  = PROPERTY_TYPES.find(p => p.value === propertyType)?.label ?? propertyType;
  const metricsCatLabel = buildingCategoryId ? (BUILDING_CATEGORIES.find(c => c.value === buildingCategoryId)?.label?.trim() ?? '') : '';
  const metricsPeriod   = currentPeriod?.label ? `12 Month Period Ending ${expandMonth(currentPeriod.label.split('–')[1]?.trim() ?? '')}` : '';
  const metricsSubtitle = [locationName, [metricsCatLabel, metricsPtLabel].filter(Boolean).join(' '), metricsPeriod].filter(Boolean).join(' | ');

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 61px)' }}>

      {/* Left accordion */}
      <div style={{
        width: 220, flexShrink: 0, borderRight: '1px solid #ddd',
        padding: 0, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 0,
        background: '#f6f5f1', borderTop: '1px solid #ddd',
      }}>
        <div style={{ padding: '1rem', fontFamily: "'Montserrat', sans-serif", fontSize: 9, fontWeight: 700, color: '#958945', letterSpacing: 3 }}>ACTIVE MARKETS</div>
        {(() => {
          const activeMarketId = mode === 'market' ? id : (owningMarketIdRef.current ?? -1);
          return markets
            .filter(m => m.hasAccess)
            .sort((a, b) => a.id === activeMarketId ? -1 : b.id === activeMarketId ? 1 : 0)
            .map(market => {
          const isExpanded = expandedMarkets.includes(market.id);
          const isActiveMarket = mode === 'market' && market.id === id;
          const neighborhoods = neighborhoodsByMarket[market.id] ?? [];

          return (
            <React.Fragment key={market.id}>
              <div
                onClick={() => { setExpandedMarkets([market.id]); navigate(`/market/${market.id}`); }}
                style={{
                  padding: '0.75rem',
                  cursor: 'pointer',
                  backgroundColor: isActiveMarket ? ACTIVE_BG : '#f6f5f1',
                  color: isActiveMarket ? '#ffffff' : '#403B36',
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: isActiveMarket ? 'bold' : 400, fontSize: 15,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  userSelect: 'none',
                  borderBottom: '1px solid #ddd',
                  boxShadow: isActiveMarket ? 'inset 3px 0 0 #bfb261' : 'none',
                }}
              >
                <span>{market.name}</span>
                <span
                  onClick={e => handleMarketToggle(e, market.id)}
                  style={{ fontSize: 10, marginLeft: 6, padding: '0 0.25rem', cursor: 'pointer' }}
                >
                  {isExpanded ? '▲' : '▼'}
                </span>
              </div>

              {isExpanded && neighborhoods.map(n => {
                const isActive = mode === 'neighborhood' && n.id === id;
                return (
                  <div
                    key={n.id}
                    onClick={() => { if (n.hasAccess) { setExpandedMarkets([market.id]); navigate(`/neighborhood/${n.id}`); } }}
                    title={n.hasAccess ? `Click to view ${n.name} insights` : `Click to add ${n.name} to your subscription`}
                    style={{
                      padding: '0.75rem 0.75rem 0.75rem 1.5rem',
                      cursor: n.hasAccess ? 'pointer' : 'default',
                      background: isActive ? ACTIVE_BG : !n.hasAccess ? LOCKED_BG : '#f6f5f1',
                      color: isActive ? ACTIVE_TEXT : !n.hasAccess ? LOCKED_TEXT : '#403B36',
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: isActive ? 'bold' : 400,
                      fontSize: 13,
                      borderBottom: '1px solid #ddd',
                      boxShadow: isActive ? 'inset 3px 0 0 #bfb261' : 'none',
                    }}
                  >
                    {n.name}
                  </div>
                );
              })}

              {isExpanded && neighborhoods.length === 0 && (
                <div style={{ padding: '0.45rem 0.75rem', color: '#aaa', fontSize: 13, fontFamily: "'Montserrat', sans-serif", borderBottom: '1px solid #ddd' }}>
                  Loading...
                </div>
              )}
            </React.Fragment>
          );
        });
        })()}
        <div style={{ marginTop: 'auto', padding: '1rem' }}>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 8, fontWeight: 400, color: '#8C877D', letterSpacing: 2.5 }}>POWERED BY</div>
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 10, fontWeight: 700, color: '#736E66', letterSpacing: 2 }}>LINK ANALYTICS</div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0, background: '#ffffff', display: 'flex', flexDirection: 'column' }}>

        {/* Hero */}
        <div style={{
          height: 184,
          backgroundImage: photoUrl
            ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${photoUrl})`
            : undefined,
          backgroundColor: '#ccc',
          backgroundSize: 'cover', backgroundPosition: 'center',
          display: 'flex', alignItems: 'flex-end',
          padding: '0 2rem 0.25rem', flexShrink: 0, overflow: 'hidden',
        }}>
          <span
            onClick={() => {
              const ptParam  = propertyType !== 'C' ? `?propertyType=${propertyType}` : '';
              const catParam = buildingCategoryId ? `${ptParam ? '&' : '?'}buildingCategoryId=${buildingCategoryId}` : '';
              const base     = mode === 'neighborhood' ? `/neighborhood/${id}` : `/market/${id}`;
              navigate(`${base}${ptParam}${catParam}`);
            }}
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '64pt',
              fontWeight: 'bold',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: 0.6,
              letterSpacing: '-1px',
              cursor: 'pointer',
            }}
          >
            {locationName}
          </span>
        </div>

        <div style={{ padding: '2rem' }}>

          {/* Property Type / Building Category filters */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
            {propertyType === 'C' && (
              <select
                value={buildingCategoryId ?? 0}
                onChange={e => { const v = Number(e.target.value); setBuildingCategoryId(v === 0 ? null : v); }}
                style={{ padding: '0.5rem', fontSize: 16 }}
              >
                {BUILDING_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            )}
            <select
              value={propertyType}
              onChange={e => { setPropertyType(e.target.value); if (e.target.value !== 'C') setBuildingCategoryId(null); }}
              style={{ padding: '0.5rem', fontSize: 16 }}
            >
              {PROPERTY_TYPES.map(pt => (
                <option key={pt.value} value={pt.value}>{pt.label}</option>
              ))}
            </select>
          </div>

          {/* Subarea filter — neighborhood mode only */}
          {mode === 'neighborhood' && subareas.length > 0 && (
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
              <select
                value={selectedSubarea?.id ?? ''}
                onChange={e => {
                  const sid = Number(e.target.value);
                  setSelectedSubarea(subareas.find(s => s.id === sid) ?? null);
                }}
                style={{ padding: '0.5rem', fontSize: 16 }}
              >
                <option value="">All Subareas</option>
                {subareas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          {/* Stats section */}
          <div ref={statsRef} style={{ marginBottom: '2.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <div style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '22pt', fontWeight: 500, color: '#000',
                }}>
                  Metrics
                </div>
                {stats && stats[0]?.label && (
                  <div style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 13, color: '#666', marginTop: '0.2rem', paddingLeft: '0.25rem',
                  }}>
                    12 Month Period Ending {expandMonth(stats[0].label.split('–')[1]?.trim() ?? '')}
                  </div>
                )}
              </div>
              {showPdfLens && !statsLoading && currentCards.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginTop: '0.25rem' }}>
                  <button
                    onClick={() => addItem({
                      entityType: mode, entityId: id, entityName: locationName,
                      chartName: 'locationStats', chartTitle: 'Metrics',
                      propertyType, buildingCategoryId,
                      yearStart: null, yearEnd: null,
                      subareaId: selectedSubarea?.id ?? null, subareaName: selectedSubarea?.name ?? null,
                      sortOrder: null,
                    })}
                    style={{ padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 6, background: '#fff', fontFamily: "'Montserrat', sans-serif" }}
                  >
                    + Add to Lens
                  </button>
                  <button
                    onClick={downloadMetricsPdf}
                    style={{ padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 6, background: '#fff', fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Download PDF
                  </button>
                </div>
              )}
            </div>

            {statsLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  border: '4px solid #e0dbd7', borderTopColor: '#ba9394',
                  animation: 'prism-spin 0.75s linear infinite',
                }} />
              </div>
            )}

            {!statsLoading && currentCards.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                {currentCards.map(card => (
                  <div key={card.label} style={{
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderTop: '3px solid #958945',
                    padding: '1rem 1.25rem',
                    fontFamily: "'Montserrat', sans-serif",
                  }}>
                    <div style={{ fontSize: 13, color: '#555', marginBottom: '0.35rem' }}>{card.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#000', marginBottom: '0.35rem' }}>{card.value}</div>
                    {card.delta != null && (
                      Number(card.delta.toFixed(1)) === 0
                        ? <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>No Change vs Previous Period</div>
                        : <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor(card.delta) }}>{fmtDelta(card.delta)} vs Previous Period</div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Hidden off-screen block used for PDF capture */}
            <div ref={statsPdfRef} style={{ position: 'absolute', left: -9999, top: -9999, width: 1060, padding: '1rem', background: '#fff' }}>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 24, fontWeight: 500, marginBottom: 8 }}>Metrics</div>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#66635F', marginBottom: 16 }}>{metricsSubtitle}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              {currentCards.map(card => (
                <div key={card.label} style={{
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderTop: '3px solid #958945',
                  padding: '1rem 1.25rem',
                  fontFamily: "'Montserrat', sans-serif",
                }}>
                  <div style={{ fontSize: 13, color: '#555', marginBottom: '0.35rem' }}>{card.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#000', marginBottom: '0.35rem' }}>{card.value}</div>
                  {card.delta != null && (
                    Number(card.delta.toFixed(1)) === 0
                      ? <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>No Change vs Previous Period</div>
                      : <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor(card.delta) }}>{fmtDelta(card.delta)} vs Previous Period</div>
                  )}
                </div>
              ))}
              </div>
            </div>
          </div>

          {/* Charts grid */}
          {chartOptions.length > 0 && (
            <div style={{ marginBottom: '2.5rem' }}>
              <div style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '22pt', fontWeight: 500, color: '#000',
                marginBottom: '1rem',
              }}>
                Insights
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '1rem',
              }}>
                {chartOptions.filter(c => {
                  const condoOnly = ['salesDistribution', 'salesPSF', 'luxVsTraditional', 'closingNow'].includes(c.name);
                  return !condoOnly || propertyType === 'C';
                }).map(chart => {
                  const ptParam  = propertyType !== 'C' ? `&propertyType=${propertyType}` : '';
                  const catParam = buildingCategoryId ? `&buildingCategoryId=${buildingCategoryId}` : '';
                  const subParam = mode === 'neighborhood' && selectedSubarea ? `&subareaId=${selectedSubarea.id}` : '';
                  const dashUrl = mode === 'neighborhood'
                    ? `/dashboard?marketId=${owningMarketIdRef.current}&neighborhoodId=${id}&chart=${chart.name}${ptParam}${catParam}${subParam}`
                    : `/dashboard?marketId=${id}&chart=${chart.name}${ptParam}${catParam}`;
                  return (
                    <div
                      key={chart.id}
                      onClick={() => navigate(dashUrl)}
                      style={{
                        background: '#fff',
                        border: '1px solid #ddd',
                        borderLeft: '10px solid #DDE5E6',
                        padding: '1rem 1.25rem',
                        cursor: 'pointer',
                        fontFamily: "'Montserrat', sans-serif",
                        display: 'flex', flexDirection: 'column',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: '0.35rem', color: '#000' }}>
                        {chart.title}
                      </div>
                      <div style={{ textAlign: 'right', marginTop: 'auto', fontSize: 12, fontWeight: 600, color: '#958945' }}>View chart</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Neighborhood Overview (market pages only) */}
          {mode === 'market' && (
            <div ref={neighborhoodTableRef} style={{ marginBottom: '2.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <div style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '22pt', fontWeight: 500, color: '#000',
                  }}>
                    Neighborhoods
                  </div>
                  {stats && stats[0]?.label && (
                    <div style={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: 13, color: '#666', marginTop: '0.2rem', paddingLeft: '0.25rem',
                    }}>
                      12 Month Period Ending {expandMonth(stats[0].label.split('–')[1]?.trim() ?? '')}
                    </div>
                  )}
                </div>
                {showPdfLens && neighborhoodOverview && neighborhoodOverview.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginTop: '0.25rem' }}>
                    <button
                      onClick={() => addItem({
                        entityType: 'market', entityId: id, entityName: locationName,
                        chartName: 'neighborhoodsOverview', chartTitle: 'Neighborhoods',
                        propertyType, buildingCategoryId,
                        yearStart: null, yearEnd: null,
                        subareaId: null, subareaName: null, sortOrder: null,
                      })}
                      style={{ padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 6, background: '#fff', fontFamily: "'Montserrat', sans-serif" }}
                    >
                      + Add to Lens
                    </button>
                    <button
                      onClick={downloadNeighborhoodsPdf}
                      style={{ padding: '0.4rem 1rem', fontSize: 14, cursor: 'pointer', border: '1px solid #ddd', borderRadius: 6, background: '#fff', fontFamily: "'Montserrat', sans-serif" }}
                    >
                      Download PDF
                    </button>
                  </div>
                )}
              </div>

              {neighborhoodOverview === null && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    border: '4px solid #e0dbd7', borderTopColor: '#ba9394',
                    animation: 'prism-spin 0.75s linear infinite',
                  }} />
                </div>
              )}

              {neighborhoodOverview && neighborhoodOverview.length > 0 && (() => {
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#DCE4E5', color: '#000', textAlign: 'left' }}>
                        <th style={thStyle}>Neighborhood</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Avg Price</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Median Price</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Avg PSF</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Median PSF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...neighborhoodOverview].sort((a, b) =>
                        a.neighborhoodName.localeCompare(b.neighborhoodName)
                      ).map((row, i) => {
                        const nhName = row.neighborhoodName;
                        const cell = (value: number | null, delta: number | null) => {
                          const formatted = fmtPrice(value);
                          if (delta == null || Number(delta.toFixed(1)) === 0) {
                            return <span style={{ color: '#000' }}>{formatted}</span>;
                          }
                          const color = delta > 0 ? '#1CA11C' : 'rgb(214,10,34)';
                          const arrow = delta > 0 ? '▲' : '▼';
                          return <span><span style={{ color }}>{arrow} </span><span style={{ color: '#000' }}>{formatted}</span></span>;
                        };
                        return (
                          <tr
                            key={row.neighborhoodId}
                            onClick={() => navigate(`/neighborhood/${row.neighborhoodId}`)}
                            style={{
                              background: i % 2 === 0 ? '#fff' : '#F0EEEA',
                              cursor: 'pointer',
                            }}
                          >
                            <td style={tdStyle}>{nhName}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{cell(row.avgPrice, row.deltaAvgPrice)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{cell(row.medianPrice, row.deltaMedianPrice)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{cell(row.avgPsf, row.deltaAvgPsf)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{cell(row.medianPsf, row.deltaMedianPsf)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}

          {/* Off-screen capture for Neighborhoods PDF */}
          {mode === 'market' && neighborhoodOverview && neighborhoodOverview.length > 0 && (
            <div ref={neighborhoodPdfRef} style={{ position: 'absolute', left: -9999, top: -9999, width: 1060, padding: '1rem', background: '#fff' }}>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 24, fontWeight: 500, marginBottom: 8 }}>Neighborhoods</div>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#66635F', marginBottom: 16 }}>{metricsSubtitle}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#DCE4E5', color: '#000', textAlign: 'left' }}>
                    <th style={thStyle}>Neighborhood</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Avg Price</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Median Price</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Avg PSF</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Median PSF</th>
                  </tr>
                </thead>
                <tbody>
                  {[...neighborhoodOverview].sort((a, b) => a.neighborhoodName.localeCompare(b.neighborhoodName)).map((row, i) => {
                    const nhCell = (value: number | null, delta: number | null) => {
                      const formatted = fmtPrice(value);
                      if (delta == null || Number(delta.toFixed(1)) === 0) return <span style={{ color: '#000' }}>{formatted}</span>;
                      const color = delta > 0 ? '#1CA11C' : 'rgb(214,10,34)';
                      return <span><span style={{ color }}>{delta > 0 ? '▲' : '▼'} </span><span style={{ color: '#000' }}>{formatted}</span></span>;
                    };
                    return (
                      <tr key={row.neighborhoodId} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
                        <td style={tdStyle}>{row.neighborhoodName}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{nhCell(row.avgPrice,    row.deltaAvgPrice)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{nhCell(row.medianPrice, row.deltaMedianPrice)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{nhCell(row.avgPsf,      row.deltaAvgPsf)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{nhCell(row.medianPsf,   row.deltaMedianPsf)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Buildings table */}
          {propertyType === 'C' && buildings === null && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                border: '4px solid #e0dbd7', borderTopColor: '#ba9394',
                animation: 'prism-spin 0.75s linear infinite',
              }} />
            </div>
          )}
          {propertyType === 'C' && buildings && buildings.length > 0 && (() => {
            const ROWS_PER_PAGE = 10;
            const handleBuildingSort = (col: typeof buildingSortCol) => {
              if (col === buildingSortCol) {
                setBuildingSortDir(d => d === 'asc' ? 'desc' : 'asc');
              } else {
                setBuildingSortCol(col);
                setBuildingSortDir('asc');
              }
              setBuildingPage(1);
            };
            const dir = buildingSortDir === 'asc' ? 1 : -1;
            const sortedBuildings = [...buildings].sort((a, b) => {
              switch (buildingSortCol) {
                case 'building':      return dir * sortableBuildingName(a.building).localeCompare(sortableBuildingName(b.building));
                case 'neighborhood':  return dir * (a.neighborhood ?? '').localeCompare(b.neighborhood ?? '');
                case 'address': {
                  if (!a.buildingAddress && !b.buildingAddress) return 0;
                  if (!a.buildingAddress) return 1;
                  if (!b.buildingAddress) return -1;
                  return dir * a.buildingAddress.localeCompare(b.buildingAddress);
                }
                case 'units': {
                  if (a.units == null && b.units == null) return 0;
                  if (a.units == null) return 1;
                  if (b.units == null) return -1;
                  return dir * (a.units - b.units);
                }
                case 'appreciation': {
                  if (a.annualizedAppreciation == null && b.annualizedAppreciation == null) return 0;
                  if (a.annualizedAppreciation == null) return 1;
                  if (b.annualizedAppreciation == null) return -1;
                  return dir * (a.annualizedAppreciation - b.annualizedAppreciation);
                }
                default: return 0;
              }
            });
            const sortTh = (col: typeof buildingSortCol, label: string, align: 'left' | 'center' | 'right' = 'left') => (
              <th
                onClick={() => handleBuildingSort(col)}
                style={{
                  ...thStyle,
                  textAlign: align,
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}{buildingSortCol === col ? (buildingSortDir === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
            );
            const totalPages = Math.ceil(sortedBuildings.length / ROWS_PER_PAGE);
            const pagedBuildings = sortedBuildings.slice((buildingPage - 1) * ROWS_PER_PAGE, buildingPage * ROWS_PER_PAGE);
            const pageNums: (number | '...')[] = totalPages <= 7
              ? Array.from({ length: totalPages }, (_, i) => i + 1)
              : buildingPage <= 4
                ? [1, 2, 3, 4, 5, '...', totalPages]
                : buildingPage >= totalPages - 3
                  ? [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
                  : [1, '...', buildingPage - 1, buildingPage, buildingPage + 1, '...', totalPages];
            const pgBtnStyle = (active: boolean): React.CSSProperties => ({
              padding: '0.25rem 0.6rem', fontFamily: "'Montserrat', sans-serif", fontSize: 13,
              border: '1px solid #ddd', borderRadius: 3, cursor: 'pointer',
              background: active ? '#958945' : '#fff', color: active ? '#fff' : '#333',
              fontWeight: active ? 700 : 400,
            });
            return (
              <div>
                <div style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '22pt', fontWeight: 500, color: '#000',
                  marginBottom: '1rem',
                }}>
                  Buildings
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#DCE4E5', color: '#000', textAlign: 'left' }}>
                      {sortTh('building', 'Building')}
                      {mode === 'market' && sortTh('neighborhood', 'Neighborhood')}
                      {sortTh('address', 'Address')}
                      {sortTh('units', 'Units', 'center')}
                      {sortTh('appreciation', 'Ann. Appreciation', 'right')}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedBuildings.map((row, i) => (
                      <tr
                        key={row.buildingId}
                        onClick={() => navigate(`/building/${row.buildingId}`)}
                        style={{
                          background: i % 2 === 0 ? '#fff' : '#F0EEEA',
                          cursor: 'pointer',
                        }}
                      >
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 500 }}>{row.building}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{row.buildingCategory}</div>
                        </td>
                        {mode === 'market' && <td style={tdStyle}>{row.neighborhood}</td>}
                        <td style={tdStyle}>{row.buildingAddress ?? '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.units ?? '—'}</td>
                        <td style={{
                          ...tdStyle,
                          textAlign: 'right',
                          fontWeight: 600,
                          color: row.annualizedAppreciation == null ? '#555'
                               : row.annualizedAppreciation < 0 ? 'rgb(214,10,34)' : '#1CA11C',
                        }}>
                          {row.annualizedAppreciation != null
                            ? `${(row.annualizedAppreciation * 100).toFixed(1)}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', justifyContent: 'center', marginTop: '1.25rem' }}>
                    <button onClick={() => setBuildingPage(p => Math.max(1, p - 1))} disabled={buildingPage === 1} style={{ ...pgBtnStyle(false), opacity: buildingPage === 1 ? 0.4 : 1 }}>← Prev</button>
                    {pageNums.map((n, i) =>
                      n === '...'
                        ? <span key={`ellipsis-${i}`} style={{ padding: '0 0.25rem', fontSize: 13, color: '#999' }}>…</span>
                        : <button key={n} onClick={() => setBuildingPage(n)} style={pgBtnStyle(n === buildingPage)}>{n}</button>
                    )}
                    <button onClick={() => setBuildingPage(p => Math.min(totalPages, p + 1))} disabled={buildingPage === totalPages} style={{ ...pgBtnStyle(false), opacity: buildingPage === totalPages ? 0.4 : 1 }}>Next →</button>
                  </div>
                )}
              </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
}
